/* ==========================================================================
   plan-clock.js — THE single date engine for every Layby USA surface.

   Extracted verbatim out of occasions.js (2026-09-04) so the homepage hero,
   the occasion tiles, the pace instrument and the fee ledger all answer the
   same question the same way. Before this there were two copies of the maths
   — one here, one inline in sections/main-cart.liquid — and two copies of a
   schedule rule is how a storefront starts quoting a date the checkout will
   not honour.

   The rules, in one place:

     first payment  = taken the day the order is placed (orderDay: the
                      America/New_York day, the day the order backend dates it
                      and the checkout draws from)
     second payment = one cadence after the first, on the same weekday: a week
                      on, or two on a plan paid every two weeks (secondPayment).
                      No payday to pick since 2026-10-13 — "It just pays on their
                      frequency select from that checkout payment day"
     payments       = ceil(price / weekly), one per cadence period, the first
                      of them on the order day
     ships          = the ship window counted forward from the FINAL payment
                      ("20 business days" skips weekends; "3 weeks" is calendar)
     lands          = ships + a transit allowance, which is the carrier's, not
                      ours — never quote it as a delivery promise

   Nothing in here is a promise about delivery. The obligation is DISPATCH
   within the ship window; callers say "lands" or "ships", never "arrives by".

   Loaded with `defer` ahead of its consumers. It is idempotent — two sections
   may both request it and the second execution is a no-op.
   ========================================================================== */
(function () {
  'use strict';

  if (window.LaybyClock) return;

  var DAY_MS = 86400000;
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  var DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var PAYDAY = 3; // Wednesday — the earliest payable day at checkout
  /** The calendar the order backend and the debits run on. */
  var REGION_TIME_ZONE = 'America/New_York';

  /** The viewer's own calendar day, as a local-midnight Date. */
  function today() { var d = new Date(); d.setHours(0, 0, 0, 0); return d; }
  function addDays(d, n) { var x = new Date(d.getTime()); x.setDate(x.getDate() + n); return x; }

  /**
   * The region's calendar day (America/New_York), as a local-midnight Date.
   * The viewer's own day is the fallback for an engine without time-zone data.
   */
  function regionToday() {
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: REGION_TIME_ZONE, year: 'numeric', month: 'numeric', day: 'numeric'
      }).formatToParts(new Date());
      var part = function (type) {
        for (var i = 0; i < parts.length; i++) if (parts[i].type === type) return +parts[i].value;
        return NaN;
      };
      var d = new Date(part('year'), part('month') - 1, part('day'));
      if (!isNaN(d.getTime())) return d;
    } catch (e) { /* unknown zone or no Intl: fall through to the viewer's day */ }
    return today();
  }

  /**
   * The day an order placed now is placed on, which is the day its first
   * payment is taken: the America/New_York day. The order backend dates the
   * order and every payment after it on that calendar, and the checkout draws
   * the plan from the same day (regionToday in layaway-form.js), so a viewer
   * ahead of Eastern time (staff in New Zealand) sees the weekday they would
   * actually be billed on. Until 2026-10-13 this was the later of the Eastern
   * day and the viewer's own, for the payday cards, which are gone.
   */
  function orderDay() {
    return regionToday();
  }

  /**
   * The second payment's date: one interval after the first, on the same
   * weekday, as the checkout schedules it (layaway-form.js scheduleDates).
   * Every later payment is a whole interval after it.
   *
   *   from      the order day (see orderDay)
   *   dow       ignored since 2026-10-13 (there is no payday to pick); kept so
   *             callers that still pass one keep working
   *   interval  days between payments: 7 or 14. Default 7
   *
   * Sat 10 Oct, weekly → Sat 17 Oct; Mon 5 Oct, every two weeks → Mon 19 Oct.
   */
  function secondPayment(from, dow, interval) {
    return addDays(from, interval || 7);
  }

  /**
   * The date of payment `i` of a plan: 0 is the first, taken on `opts.from`
   * (default the order day); 1 is secondPayment(); each later one is a whole
   * interval after that.
   *
   *   opts.from          the order day. Default orderDay()
   *   opts.payday        3-5 (Wed-Fri). Default Wednesday
   *   opts.cadenceWeeks  1 = weekly, 2 = every two weeks. Default 1
   */
  function paymentDate(i, opts) {
    var o = opts || {};
    var first = o.from || orderDay();
    if (i <= 0) return first;
    var step = 7 * (o.cadenceWeeks || 1);
    return addDays(secondPayment(first, o.payday, step), (i - 1) * step);
  }

  /**
   * How many payments of a plan whose first payment is on `opts.from` (default
   * the order day) fall on or before `by`. 0 when `by` is before the first.
   * Days are rounded, so a daylight-saving change can never lose a payment.
   */
  function paymentsBy(by, opts) {
    var o = opts || {};
    var first = o.from || orderDay();
    if (by < first) return 0;
    var step = 7 * (o.cadenceWeeks || 1);
    var next = secondPayment(first, o.payday, step);
    if (by < next) return 1;
    return 2 + Math.floor(Math.round((by - next) / DAY_MS) / step);
  }

  /** Calendar days forward (or back) skipping Saturday and Sunday. */
  function addBusinessDays(d, n) {
    var x = new Date(d.getTime()), left = Math.abs(n), step = n < 0 ? -1 : 1;
    while (left > 0) {
      x = addDays(x, step);
      var g = x.getDay();
      if (g !== 0 && g !== 6) left--;
    }
    return x;
  }

  /**
   * The next payable day on or after `from`. Returns `from` itself when it
   * already falls on the payday. Kept for any caller that wants a weekday;
   * plan dates come from orderDay and secondPayment, not from this.
   */
  function nextPayday(from, dow) {
    var want = typeof dow === 'number' ? dow : PAYDAY;
    var x = new Date(from.getTime());
    while (x.getDay() !== want) x = addDays(x, 1);
    return x;
  }

  function fmt(d) { return MONTHS[d.getMonth()] + ' ' + d.getDate(); }
  /** "Wed, Oct 14" */
  function fmtDay(d) { return DAY_NAMES[d.getDay()].slice(0, 3) + ', ' + fmt(d); }
  function fmtLong(d) { return d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear(); }
  function iso(d) {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }
  function money(n) { return '$' + String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }

  /** The nth given weekday of a month — Mother's Day, Thanksgiving and friends. */
  function nthWeekday(year, month, weekday, nth) {
    var d = new Date(year, month, 1);
    while (d.getDay() !== weekday) d = addDays(d, 1);
    return addDays(d, (nth - 1) * 7);
  }

  /**
   * The ship window as something that can be counted forward or back.
   * Understands "20 business days", "1–3 weeks" (far end), "3 weeks", "10 days".
   * Falls back to 20 business days, which is the contractual window — an
   * unparseable setting must never shorten what we appear to promise.
   */
  function parseShipWindow(text) {
    var w = String(text || ''), m;
    if ((m = /(\d+)\s*business/i.exec(w))) return { business: true, n: +m[1] };
    if ((m = /(\d+)\s*(?:–|-|to)\s*(\d+)\s*weeks?/i.exec(w))) return { business: false, n: +m[2] * 7 };
    if ((m = /(\d+)\s*weeks?/i.exec(w))) return { business: false, n: +m[1] * 7 };
    if ((m = /(\d+)\s*(?:–|-|to)\s*(\d+)\s*days?/i.exec(w))) return { business: false, n: +m[2] };
    if ((m = /(\d+)\s*days?/i.exec(w))) return { business: false, n: +m[1] };
    return { business: true, n: 20 };
  }
  function shipForward(d, w) { return w.business ? addBusinessDays(d, w.n) : addDays(d, w.n); }
  function shipBackward(d, w) { return w.business ? addBusinessDays(d, -w.n) : addDays(d, -w.n); }

  /**
   * A whole plan for `price`, in dollars.
   *
   * opts.weekly        instalment in dollars (required)
   * opts.ship          a parsed ship window from parseShipWindow()
   * opts.payday        3-5 (Wed-Fri), default Wednesday
   * opts.cadenceWeeks  1 = weekly, 2 = every two weeks. Default 1.
   * opts.breaks        skipped payments to add to the term. Default 0.
   * opts.from          the order day, when the first payment is taken.
   *                    Default orderDay().
   *
   * Returns { payments, first, next, last, ship, weeks }: `first` is the order
   * day, `next` the second payment (see secondPayment) and `last` the final
   * one. `weeks` is the span in weeks from the first payment to the last, so a
   * caller can compare it against the change ceiling without redoing the
   * arithmetic. The count is the same whatever the dates: the first payment is
   * one of the `payments`, not an extra.
   */
  function plan(price, opts) {
    var o = opts || {};
    var payments = Math.max(1, Math.ceil(price / o.weekly));
    var dates = { from: o.from || orderDay(), payday: o.payday, cadenceWeeks: o.cadenceWeeks };
    var first = dates.from;
    // A break pushes the final payment out by one interval; a one-payment
    // plan has nothing after today to push.
    var last = paymentDate(payments > 1 ? payments - 1 + (o.breaks || 0) : 0, dates);
    return {
      payments: payments,
      first: first,
      next: paymentDate(1, dates),
      last: last,
      ship: shipForward(last, o.ship || parseShipWindow(null)),
      weeks: Math.round((last - first) / (7 * DAY_MS))
    };
  }

  /**
   * The last day a final payment can fall on and still have the order land
   * before `occasion`: the transit allowance and the ship window counted back
   * from the date.
   *
   * opts.ship         a parsed ship window. Default the contractual window.
   * opts.transitDays  the carrier's allowance. Default 5.
   */
  function payoffDeadline(occasion, opts) {
    var o = opts || {};
    var transit = typeof o.transitDays === 'number' ? o.transitDays : 5;
    return shipBackward(addDays(occasion, -transit), o.ship || parseShipWindow(null));
  }

  /**
   * The biggest plan started on the order day whose order still lands before
   * `occasion`: every payment that falls on or before the payoff deadline,
   * today's included. Returns dollars, 0 if nothing fits — callers must handle
   * 0 rather than printing "up to $0".
   */
  function capBefore(occasion, opts) {
    var o = opts || {};
    return paymentsBy(payoffDeadline(occasion, o), o) * o.weekly;
  }

  function weeksAway(d, from) {
    return Math.max(0, Math.round((d - (from || today())) / (7 * DAY_MS)));
  }

  /** The plan JSON snippets/layaway-plan.liquid renders as `schedule-data`. */
  function readPlan(root) {
    var node = (root || document).querySelector('script[data-layaway-plan]');
    if (!node) return {};
    try { return JSON.parse(node.textContent); } catch (e) { return {}; }
  }

  window.LaybyClock = {
    DAY_MS: DAY_MS,
    MONTHS: MONTHS,
    DAYS: DAYS,
    DAY_NAMES: DAY_NAMES,
    PAYDAY: PAYDAY,
    today: today,
    regionToday: regionToday,
    orderDay: orderDay,
    addDays: addDays,
    addBusinessDays: addBusinessDays,
    nextPayday: nextPayday,
    secondPayment: secondPayment,
    paymentDate: paymentDate,
    paymentsBy: paymentsBy,
    payoffDeadline: payoffDeadline,
    fmt: fmt,
    fmtDay: fmtDay,
    fmtLong: fmtLong,
    iso: iso,
    money: money,
    nthWeekday: nthWeekday,
    parseShipWindow: parseShipWindow,
    shipForward: shipForward,
    shipBackward: shipBackward,
    plan: plan,
    capBefore: capBefore,
    weeksAway: weeksAway,
    readPlan: readPlan
  };
})();
